import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { postCertsList } from '../../apiCalls';
import { GoogleAnalyticsContext } from '../../GoogleAnalytics';
import { CategoryOfGoodsTooltip } from '../../UnifiedLoad/ProductInfo/Tooltips';
import { useStepper } from '../../Stepper';
import cargo from '../assets/catofgoods.png';
import CogModal from './CategoryOfGoodsModal';
import StepWrapper from './StepWrapper';

export function CogPage() {
  const { stepForward, localData, setDataByKey, activeStepKey, gotoStep, unvisitStep } = useStepper();
  const [showCogModal, setShowCogModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const modalProps = {
    title: 'Licences and Certificates',
    body: (
      <>
        <p className="bold">Warning - Licences or Certificates you've uploaded</p>
        <p>
          If you proceed to change the Category of Goods, any Licences or Certificates you may have uploaded will be
          reset, are you sure you wish to proceed with this change?
        </p>
      </>
    ),
    showContinue: true,
  };

  const anyControlledGoods = useMemo(() => {
    return localData.selectedCategoryOfGoods.some((item) => item.controlled);
  }, [localData.selectedCategoryOfGoods]);

  const handleClick = (e) => {
    e.preventDefault();
    setError('');
    if (localData.documents?.length > 0) {
      setShowConfirmModal(true);
    } else {
      setShowCogModal(true);
    }
  };

  const clearCheckedCategory = () => {
    setDataByKey(
      'categoryOptions',
      localData.categoryOptions.map((category) => {
        return { ...category, checked: false };
      })
    );
  };

  const nextPage = async () => {
    if (localData.confirmedNoCG == '' || localData.confirmedNoCG === undefined) {
      setError('Please confirm if your consignment contains controlled goods.');
      return;
    }
    if (localData.confirmedNoCG == 'NCG') {
      unvisitStep('9. Documents');
    }

    if (localData.confirmedNoCG == 'CG') {
      const fd = new FormData();
      const ids = localData.selectedCategoryOfGoods.map((good) => parseInt(good.id));
      fd.append('controlled', ids);
      fd.append('movementType', localData.movementType);
      fd.append('tad', localData.tad);
      const data = await postCertsList(fd);
      if (localData.documents.length === 0) {
        const formatted = Object.keys(data[0])
          .filter((key) => !key.includes('Tooltip'))
          .map((key) => {
            return { name: key, required: data[0][key], condition: data[0][key + ' Tooltip'] };
          });
        setDataByKey('certsList', formatted);
      }
    }

    setError('');
    stepForward();
  };

  const previousPage = () => {
    setError('');
    if (localData.movementType === 'Export') {
      gotoStep('4. AdditionalRequirements');
    } else if (localData.movementType === 'Import') {
      gotoStep('3. Entry Port');
    }
  };

  const isInitialized = useRef(false);
  const ga = useContext(GoogleAnalyticsContext);

  useEffect(() => {
    if (ga.gaInitialized) {
      if (activeStepKey === '5. CategoryOfGoods' && isInitialized.current === false) {
        isInitialized.current = true;
        window?.gtag?.('event', '5. CategoryOfGoods', {
          event_category: 'EPO',
        });
      }
    }
  }, [activeStepKey, ga.gaInitialized]);

  const [error, setError] = useState('');

  return (
    <StepWrapper
      stepTitle="Category of Goods"
      stepText="Category of Goods"
      stepKey="5. CategoryOfGoods"
      handleNextClick={nextPage}
      handleBackClick={previousPage}
      hideUnlessVisited
    >
      <div className="d-flex step-banner justify-content-center flex-row pt-4">
        <img src={cargo} width="150" height="150" alt="port" className="  mb-4" />

        <CategoryOfGoodsTooltip />
      </div>

      <div className="d-flex flex-column align-self-center " style={{ marginTop: '32px' }}>
        <span style={{ maxWidth: '400px', fontWeight: '500' }}>
          Select ALL categories that describe your shipment.{' '}
        </span>
        <span className="d-flex flex-column align-self-center  " style={{ maxWidth: '280px', fontWeight: '500' }}>
          (Multiple selections are allowed)
        </span>
      </div>

      <div className="d-flex justify-content-center ">
        {(localData.confirmedNoCG == '' || localData.confirmedNoCG === undefined) && (
          <button
            type="button"
            data-testid="selectGoods-button"
            style={{ padding: '0.5rem 1.25rem' }}
            className="doc-add-btn gap-2 text-eoriblue"
            onClick={(e) => {
              setError('');
              setShowCogModal(true);
            }}
          >
            Select Goods
          </button>
        )}
        {(localData.confirmedNoCG == 'NCG' || anyControlledGoods) && (
          <button
            type="button"
            style={{ padding: '0.5rem 1.25rem', width: '200px', fontWeight: '400', fontSize: '1.1rem' }}
            className={'doc-add-btn gap-2 text-eoriblue light-green-btn mt-3'}
            onClick={handleClick}
          >
            {anyControlledGoods ? 'Controlled' : 'Non-Controlled'}{' '}
            {(anyControlledGoods || localData.confirmedNoCG == 'NCG') && <div className="tick-success"></div>}
          </button>
        )}
      </div>

      <div className="d-flex justify-content-center mt-auto mb-1" style={{ maxWidth: '468px' }}>
        {localData.confirmedNoCG == 'NCG' && (
          <span className="general-text">
            This shipment <b>DOES NOT</b> contain goods that are controlled (or require a license).
          </span>
        )}
        {anyControlledGoods && (
          <span className="general-text">
            This shipment <b>DOES</b> contain goods that are controlled (or require a license).
          </span>
        )}
        {error && (
          <div className="alert-danger px-3 py-1 w-100 text-center mt-1" style={{ borderRadius: '10px' }}>
            {error}
          </div>
        )}
      </div>

      <CogConfirmModal
        show={showConfirmModal}
        redirectReason={modalProps}
        onHide={() => {
          setShowConfirmModal(false);
        }}
        onContinue={() => {
          localData.documents.length = 0;
          clearCheckedCategory();
          setShowConfirmModal(false);
          setShowCogModal(true);
        }}
      />
      <CogModal
        show={showCogModal}
        onSave={(values, confirmedNoCG) => {
          setDataByKey('selectedCategoryOfGoods', values);
          setDataByKey('confirmedNoCG', confirmedNoCG);
          setShowCogModal(false);
        }}
        onCancel={() => {
          setDataByKey('confirmedNoCG', '');
          setDataByKey('selectedCategoryOfGoods', []);
          setShowCogModal(false);
        }}
      />
    </StepWrapper>
  );
}

export const CogConfirmModal = ({ show, redirectReason, onHide, onContinue }) => {
  function cancelClick() {
    onHide();
  }

  return (
    <Modal show={show} centered size={'md'} onHide={onHide} animation={false}>
      <Modal.Header>
        <Modal.Title className="d-flex gap-3 align-items-center py-2">
          <div className="warning" /> <span>{redirectReason?.title}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-4">{redirectReason?.body}</Modal.Body>
      <Modal.Footer
        className="d-flex flex-row py-4 px-3 gap-3 border-top bg-light justify-content-end align-items-center"
        style={{
          borderBottomLeftRadius: '20px',
          borderBottomRightRadius: '20px',
        }}
      >
        <button className="stepper-button" onClick={cancelClick}>
          Cancel
        </button>
        {redirectReason?.showContinue && (
          <button className="stepper-button yellow" onClick={() => onContinue()}>
            Continue
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
